import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Loader2 } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useNotificationsList } from '@/hooks/useNotificationsList';
import { Button } from '@/components/ui/Button';
import { ScrollArea } from '@/components/ui/ScrollArea';

export function NotificationsDropdown() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hasSupabaseUser = Boolean(user?.authUserId ?? user?.id);
  const {
    notifications,
    isLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
  } = useNotificationsList(hasSupabaseUser && open);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        panelRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-medium text-white"
            aria-hidden
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          ref={panelRef}
          className="absolute right-0 top-full z-50 mt-2 w-80 rounded-2xl border border-white/10 bg-surface-900 shadow-xl"
          role="dialog"
          aria-label="Notifications"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <span className="text-sm font-medium text-white">Notifications</span>
            {unreadCount > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1 text-xs text-white/70 hover:text-white"
                onClick={() => markAllAsRead()}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </Button>
            ) : null}
          </div>
          <ScrollArea className="h-[min(20rem,60vh)]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-white/50" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-sm text-white/50">
                No notifications yet
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {notifications.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        markAsRead(n.id);
                      }}
                      className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5 ${
                        n.read ? 'text-white/70' : 'bg-white/5 text-white'
                      }`}
                    >
                      <span className="mt-0.5 shrink-0">
                        {n.read ? (
                          <Check className="h-4 w-4 text-white/40" />
                        ) : (
                          <span className="block h-2 w-2 rounded-full bg-accent-400" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{n.title}</p>
                        {n.body ? (
                          <p className="mt-0.5 line-clamp-2 text-xs text-white/60">
                            {n.body}
                          </p>
                        ) : null}
                        <p className="mt-1 text-[10px] text-white/40">
                          {formatNotificationDate(n.created_at)}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
          <div className="border-t border-white/10 px-4 py-2">
            <Link
              to="/app/notifications"
              onClick={() => setOpen(false)}
              className="block text-center text-xs text-accent-400 hover:underline"
            >
              View all notifications
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatNotificationDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3600_000);
  const diffDays = Math.floor(diffMs / 86400_000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}
